import { useEffect, useState } from "react";
import { filesize } from "filesize";
import styles from "../../showroomForm.css";
import Button from "../../../../../design-system/components/Button/Button.jsx";
import { Variants } from "../../../../../design-system/components/Button/Variants.jsx";
import { MODELS, useMultiModel } from "../../../../../api";
import PeertubeVideoEmbed from "./PeertubeVideoEmbed.jsx";

/**
 * Retrieves the name of the file from the provided file object.
 *
 * @param {Object} file - The file object containing file details. It may include properties such as `name`, `path`, or `data`.
 * @return {string|undefined} The file name if it can be determined, otherwise undefined.
 */
function getFileName(file) {
  if (!file) return undefined;
  if (file.name) return file.name;
  const filepath = file.path ?? file.data?.path;
  if (filepath) return filepath.substring(filepath.lastIndexOf("/") + 1);
  return undefined;
}

/**
 * Extracts and returns information about a file including its name, size, and type.
 *
 * @param {Object} file - The file object containing information about the file.
 * @param {string} [file.mimetype] - The MIME type of the file, if available.
 * @param {Object} [file.data] - Additional data object potentially containing file details.
 * @param {string} [file.data.mimetype] - MIME type of the file from the server.
 * @param {string} [file.data.uuid] - Unique identifier for server-provided files.
 * @param {number} [file.size] - Size of the file in bytes, if available directly.
 * @param {string} [file.type] - Indicates the type of the file as provided locally or from the server (e.g., "file", "link", "video").
 *
 * @return {Object} Returns an object containing the following properties:
 * - fileName {string} The name of the file.
 * - fileSize {string|undefined} The size of the file as a human-readable string if determined, otherwise undefined.
 * - fileType {string|null} The type of the file as determined from the MIME type, type field, or specific rules for cases like peertube videos.
 */
function getFileInfo(file) {
  const fileName = getFileName(file);

  let fileSize = undefined;
  try {
    fileSize = filesize(file.size || file.data?.size);
  } catch {
    // ignore
  }

  // The type can me the mimetype directly in the file or in the data if it comes from the server.
  // If directly uploaded it can also be in file.type. This is checked third because files
  // coming from the server also have file.type, but that has the value "file", "link" or "video".
  // As "video" is also given to a file that has been uploaded on peertube but we need to treat peertube videos differently, we treat that case specifically
  const fileType =
    file.mimetype ||
    file.data?.mimetype ||
    (file.data?.uuid && "peertube") ||
    file.type ||
    null;

  return {
    fileName,
    fileSize,
    fileType,
  };
}

/**
 * Renders a data preview based on the provided file type, URL, and data.
 * The preview can be an image, video, audio, or a PeerTube embedded video.
 *
 * @param {string} filetype - The type of the file (e.g., "image", "video", "audio", "peertube").
 * @param {string} fileUrl - The URL of the file to render (used for image, video, and audio previews).
 * @param {object} fileData - Additional data for the file (used for PeerTube video embeddings).
 * @returns {JSX.Element|null} Returns a JSX element representing the appropriate preview for the file type,
 * or null if the file type is not provided or handled.
 */
const renderDataPreview = (filetype, fileUrl, fileData) => {
  if (!filetype) return null;
  if (fileUrl && filetype.startsWith("image")) {
    return (
      <img
        src={fileUrl}
        aria-label="Vorschaubild für eine Datei, die von einem User hochgeladen wurde"
      />
    );
  } else if (fileUrl && filetype.startsWith("video")) {
    return (
      <video
        controls
        src={fileUrl}
        aria-label="Video-Datei, die von einem User hochgeladen wurde"
        style={{ maxHeight: "200px" }}
      >
        Videovorschau wird von diesem Browser nicht unterstützt.
      </video>
    );
  } else if (fileUrl && filetype.startsWith("audio")) {
    return (
      <audio
        controls
        src={fileUrl}
        aria-label="Audio-Datei, die von einem User hochgeladen wurde"
        style={{ maxHeight: "200px" }}
      >
        Audiovorschau wird von diesem Browser nicht unterstützt.
      </audio>
    );
  } else if (filetype.startsWith("peertube")) {
    return <PeertubeVideoEmbed data={fileData} />;
  }
};

/**
 * Component that renders a preview of an uploaded file.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.fieldKey - The key identifying the file in the form data.
 * @param {File} props.file - The file object to be previewed.
 * @param {Object} props.formData - The current form data containing the uploaded file.
 * @param {Function} props.setFormData - A state updater function to modify the form data.
 * @returns {JSX.Element} The rendered preview component.
 */
export const PreviewFile = ({ fieldKey, file, formData, setFormData }) => {
  const [modelData] = useMultiModel([
    file?.data?.path ? MODELS.S3_FILE_URL(file.data.path) : null,
  ]);
  const [fileUrl, setFileUrl] = useState(null);
  const [error, setError] = useState(null);
  const { fileName, fileSize, fileType } = getFileInfo(file);

  const value = formData[fieldKey];

  // In useEffect we fetch a preview image for any just now selected file or images uploaded already
  useEffect(() => {
    let isMounted = true; // Avoid setting state if the component unmounts
    // Only blob URLs we create here need revoking on cleanup. Tracking it in a
    // local (instead of the `fileUrl` state) keeps it out of the dep array — a
    // `createObjectURL` result changes every call, so depending on `fileUrl`
    // would re-trigger this effect endlessly ("Maximum update depth exceeded").
    let createdObjectUrl = null;

    if (fileType.startsWith("peertube")) return; // Exception for uploaded Peertube videos: They don't need an objectURL!

    /**
     * Asynchronously fetches a file preview URL for a given file input.
     */
    const fetchFilePreview = async () => {
      let objectUrl;
      try {
        // If file is directly passed, create URL immediately
        // this is the case for just selected files of any format
        objectUrl = URL.createObjectURL(value);
        createdObjectUrl = objectUrl;
      } catch {
        // The above throws an error if the file is on the server or S3, and not on the user machine.
        if (modelData?.fileUrl) {
          objectUrl = modelData.fileUrl;
        } else {
          setError(`Could not display file ${file?.data?.path}`);
        }
      }
      if (isMounted && objectUrl) {
        setFileUrl(objectUrl);
        setError(null);
      }
    };

    fetchFilePreview();

    // Cleanup: Revoke the object URL to avoid memory leaks. Only revoke a URL we
    // created ourselves — never the server/S3 `modelData.fileUrl`.
    return () => {
      isMounted = false; // Prevent state updates after unmount
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
    };
  }, [value, fileType, modelData, file?.data?.path]); // Re-run on file or getServerImage change

  const onRemove = () => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldKey]: null,
    }));
  };

  if (error) {
    console.warn(error);
  }

  // Render the file upload field with the appropriate preview and information
  return (
    <div className={styles.previewImg}>
      {!error && renderDataPreview(fileType, fileUrl, formData[fieldKey].data)}
      {fileName && <p>{fileName}</p>}
      {fileSize && (
        <p>
          <b>{fileSize}</b>
        </p>
      )}
      <Button onClick={onRemove} variant={Variants.QUATERNARY}>
        Entfernen
      </Button>
    </div>
  );
};
